import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Users, UserPlus, Shield, ShieldCheck, Trash2,
  Edit3, X, CheckCircle, Mail, AlertCircle, Copy
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAPABILITIES = [
  { key: 'can_create_amcs', label: 'Create AMCs', desc: 'Add new AMC tenants to the platform' },
  { key: 'can_edit_amcs', label: 'Edit AMCs', desc: 'Modify AMC profile, branding and module access' },
  { key: 'can_delete_amcs', label: 'Delete AMCs', desc: 'Permanently remove an AMC (destructive)' },
  { key: 'can_manage_amc_modules', label: 'Manage AMC Modules', desc: 'Enable or disable features for each AMC' },
  { key: 'can_manage_amc_users', label: 'Manage AMC Users', desc: 'Invite / list / deactivate AMC team members' },
  { key: 'can_reset_amc_passwords', label: 'Reset AMC Passwords', desc: 'Trigger a password reset for any AMC user' },
  { key: 'can_view_reports', label: 'View Reports', desc: 'Access platform-wide analytics and reports' },
  { key: 'can_manage_owner_team', label: 'Manage Owner Team', desc: 'Invite / edit / remove other owner-portal users' },
];

const emptyCaps = () => CAPABILITIES.reduce((acc, c) => { acc[c.key] = false; return acc; }, {});

const OwnerTeam = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [toast, setToast] = useState(null);

  const myCaps = user?.owner_capabilities || {};
  const canManageTeam = myCaps.is_super || myCaps.can_manage_owner_team;

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/owner/team`);
      setMembers(res.data);
    } catch (err) {
      setToast({ type: 'error', msg: 'Failed to load team: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 5000);
  };

  const handleDeactivate = async (m) => {
    if (!window.confirm(`Deactivate ${m.name} (${m.email})? They will lose access immediately.`)) return;
    try {
      await axios.delete(`${API_URL}/owner/team/${m.id}`);
      showToast('success', 'Owner removed from team');
      load();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/owner/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="flex items-center space-x-3">
                <Users className="text-blue-600" size={32} />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Owner Team Members</h1>
                  <p className="text-sm text-gray-500">Grant platform-level access to your colleagues</p>
                </div>
              </div>
            </div>
            {canManageTeam && (
              <button
                onClick={() => setShowInvite(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="invite-owner-btn"
              >
                <UserPlus size={16} className="mr-2" />
                Invite Team Member
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`} data-testid="owner-team-toast">
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span className="text-sm">{toast.msg}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading team...</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-6 py-3">Member</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Capabilities</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {members.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                      No owner team members yet. Invite one to delegate platform work.
                    </td>
                  </tr>
                )}
                {members.map((m) => {
                  const caps = m.owner_capabilities || {};
                  const isSuper = caps.is_super;
                  const activeCapsList = CAPABILITIES.filter(c => caps[c.key]).map(c => c.label);
                  return (
                    <tr key={m.id} data-testid={`owner-row-${m.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isSuper ? 'bg-purple-100' : 'bg-blue-100'
                          }`}>
                            {isSuper ? <ShieldCheck size={18} className="text-purple-600" /> : <Shield size={18} className="text-blue-600" />}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{m.name}</div>
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Mail size={12} /> {m.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {m.is_active === false ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Deactivated</span>
                        ) : isSuper ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">Super Owner</span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {isSuper ? (
                          <span className="text-xs text-purple-600 font-medium">All capabilities</span>
                        ) : activeCapsList.length === 0 ? (
                          <span className="text-xs text-gray-400">None granted</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-md">
                            {activeCapsList.slice(0, 4).map((lbl) => (
                              <span key={lbl} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{lbl}</span>
                            ))}
                            {activeCapsList.length > 4 && (
                              <span className="text-xs text-gray-500">+{activeCapsList.length - 4} more</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {!isSuper && canManageTeam && m.is_active !== false && (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setEditMember(m)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              data-testid={`edit-caps-${m.id}`}
                              title="Edit capabilities"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeactivate(m)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              data-testid={`deactivate-owner-${m.id}`}
                              title="Deactivate owner"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showInvite && (
        <InviteOwnerModal
          onClose={() => setShowInvite(false)}
          onSuccess={(payload) => {
            setShowInvite(false);
            load();
            if (payload?.temp_password) {
              showToast('success', `Invite created. Email could not be sent — temporary password: ${payload.temp_password}`);
            } else {
              showToast('success', 'Invite email sent to the new team member');
            }
          }}
        />
      )}

      {editMember && (
        <EditCapsModal
          member={editMember}
          onClose={() => setEditMember(null)}
          onSuccess={() => {
            setEditMember(null);
            load();
            showToast('success', 'Capabilities updated');
          }}
        />
      )}
    </div>
  );
};

const InviteOwnerModal = ({ onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [caps, setCaps] = useState(emptyCaps());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const toggleCap = (key) => setCaps((c) => ({ ...c, [key]: !c[key] }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/owner/team`, { email, name, capabilities: caps });
      onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to invite');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={submit}>
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <UserPlus className="text-blue-600" size={24} />
              <h2 className="text-xl font-bold text-gray-900">Invite Owner Team Member</h2>
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <X size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="invite-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="invite-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Capabilities</label>
              <p className="text-xs text-gray-500 mb-3">
                An invite email with a magic link will be sent. The invitee sets their own password on first login.
              </p>
              <div className="space-y-2 border rounded-lg p-4 bg-gray-50">
                {CAPABILITIES.map((c) => (
                  <label key={c.key} className="flex items-start gap-3 cursor-pointer hover:bg-white rounded px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={!!caps[c.key]}
                      onChange={() => toggleCap(c.key)}
                      className="mt-1"
                      data-testid={`cap-${c.key}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{c.label}</div>
                      <div className="text-xs text-gray-500">{c.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 p-6 border-t bg-gray-50">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg">
              Cancel
            </button>
            <button
              type="submit" disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
              data-testid="submit-invite"
            >
              {submitting ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditCapsModal = ({ member, onClose, onSuccess }) => {
  const [caps, setCaps] = useState(() => {
    const c = emptyCaps();
    Object.keys(member.owner_capabilities || {}).forEach((k) => { c[k] = !!member.owner_capabilities[k]; });
    return c;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const toggleCap = (key) => setCaps((c) => ({ ...c, [key]: !c[key] }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/owner/team/${member.id}/capabilities`, { capabilities: caps });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update capabilities');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={submit}>
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Edit Capabilities</h2>
              <p className="text-sm text-gray-500">{member.name} • {member.email}</p>
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <X size={18} />
            </button>
          </div>
          <div className="p-6 space-y-3">
            {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
            <div className="space-y-2 border rounded-lg p-4 bg-gray-50">
              {CAPABILITIES.map((c) => (
                <label key={c.key} className="flex items-start gap-3 cursor-pointer hover:bg-white rounded px-2 py-1.5">
                  <input
                    type="checkbox" checked={!!caps[c.key]} onChange={() => toggleCap(c.key)}
                    className="mt-1" data-testid={`edit-cap-${c.key}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{c.label}</div>
                    <div className="text-xs text-gray-500">{c.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 p-6 border-t bg-gray-50">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg">
              Cancel
            </button>
            <button
              type="submit" disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
              data-testid="save-caps"
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OwnerTeam;
